import React, { createContext, useContext, useState, useEffect } from 'react';
import authFusionService from '../services/authfusion';
import { loginErrorMessage } from '../services/loginError';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkUserSession();

    const handleUserLoaded = (event) => {
      const loadedUser = event.detail;
      if (loadedUser && !loadedUser.expired) {
        setUser({
          id: authFusionService.getUserId(),
          email: authFusionService.getUserEmail(),
          name: authFusionService.getUserName(),
          profile: authFusionService.getUserProfile(),
          accessToken: authFusionService.getAccessToken(),
        });
      }
    };

    const handleTokenRefreshed = () => {
      setUser({
        id: authFusionService.getUserId(),
        email: authFusionService.getUserEmail(),
        name: authFusionService.getUserName(),
        profile: authFusionService.getUserProfile(),
        accessToken: authFusionService.getAccessToken(),
      });
    };

    const handleUserUnloaded = () => {
      setUser(null);
    };

    const handleTokenExpired = async () => {
      try {
        const refreshedUser = await authFusionService.startSilentRenew();
        if (refreshedUser && !refreshedUser.expired) {
          return;
        }
      } catch {
        // silent renew failed
      }
      setUser(null);
      setLoading(false);
    };

    const handleSilentRenewFailed = async () => {
      const currentUser = await authFusionService.getUser();
      if (!currentUser || currentUser.expired) {
        setUser(null);
        setLoading(false);
      }
    };

    const handleUserSignedOut = () => {
      setUser(null);
      setLoading(false);
    };

    window.addEventListener('auth:userLoaded', handleUserLoaded);
    window.addEventListener('auth:tokenRefreshed', handleTokenRefreshed);
    window.addEventListener('auth:userUnloaded', handleUserUnloaded);
    window.addEventListener('auth:tokenExpired', handleTokenExpired);
    window.addEventListener('auth:silentRenewFailed', handleSilentRenewFailed);
    window.addEventListener('auth:userSignedOut', handleUserSignedOut);

    return () => {
      window.removeEventListener('auth:userLoaded', handleUserLoaded);
      window.removeEventListener('auth:tokenRefreshed', handleTokenRefreshed);
      window.removeEventListener('auth:userUnloaded', handleUserUnloaded);
      window.removeEventListener('auth:tokenExpired', handleTokenExpired);
      window.removeEventListener('auth:silentRenewFailed', handleSilentRenewFailed);
      window.removeEventListener('auth:userSignedOut', handleUserSignedOut);
    };
  }, []);

  const checkUserSession = async () => {
    try {
      let sessionUser = await authFusionService.getUser();
      // Reopened after the access token expired: try the refresh token before
      // sending the operator back to the sign-in form.
      if (sessionUser?.expired && sessionUser.refresh_token) {
        try {
          sessionUser = await authFusionService.startSilentRenew();
        } catch {
          sessionUser = null;
        }
      }
      if (sessionUser && !sessionUser.expired) {
        setUser({
          id: authFusionService.getUserId(),
          email: authFusionService.getUserEmail(),
          name: authFusionService.getUserName(),
          profile: authFusionService.getUserProfile(),
          accessToken: authFusionService.getAccessToken(),
        });
      }
    } catch (sessionError) {
      console.error('Error checking user session:', sessionError);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      await authFusionService.signinWithCredentials(email, password);
      setUser({
        id: authFusionService.getUserId(),
        email: authFusionService.getUserEmail(),
        name: authFusionService.getUserName(),
        profile: authFusionService.getUserProfile(),
        accessToken: authFusionService.getAccessToken(),
      });
    } catch (loginError) {
      setError(loginErrorMessage(loginError));
      setLoading(false);
      throw loginError;
    }
    setLoading(false);
  };

  const handleCallback = async () => {
    try {
      await authFusionService.signinRedirectCallback();
      setUser({
        id: authFusionService.getUserId(),
        email: authFusionService.getUserEmail(),
        name: authFusionService.getUserName(),
        profile: authFusionService.getUserProfile(),
        accessToken: authFusionService.getAccessToken(),
      });
    } catch (callbackError) {
      setError(callbackError.message);
      throw callbackError;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authFusionService.signoutPortal();
      setUser(null);
      setLoading(false);
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
      setUser(null);
      await authFusionService.removeUser();
      setLoading(false);
    }
  };

  const handleLogoutCallback = async () => {
    try {
      await authFusionService.signoutRedirectCallback();
      setUser(null);
    } catch (logoutCallbackError) {
      console.error('Logout callback error:', logoutCallbackError);
      setUser(null);
    }
  };

  const isAuthenticated = () => user !== null;

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    handleCallback,
    handleLogoutCallback,
    isAuthenticated: isAuthenticated(),
    getAccessToken: () => authFusionService.getAccessToken(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
